import React, { useRef, useMemo, useLayoutEffect } from "react";
import * as THREE from "three";
import { makeStone } from "../lib/textures";

// Masonry tower silhouette with two pointed (gothic) arch openings — the Brooklyn Bridge look.
function gothicTowerGeo(W, top, deckY, depth) {
  const shape = new THREE.Shape();
  shape.moveTo(-W / 2, deckY - 8);
  shape.lineTo(W / 2, deckY - 8);
  shape.lineTo(W / 2, top);
  shape.lineTo(-W / 2, top);
  shape.lineTo(-W / 2, deckY - 8);
  const base = deckY + 1.5;
  const archTop = top - 9;
  const spring = base + (archTop - base) * 0.5;
  const openHalf = W * 0.17;
  [-W * 0.22, W * 0.22].forEach((cx) => {
    const h = new THREE.Path();
    h.moveTo(cx - openHalf, base);
    h.lineTo(cx - openHalf, spring);
    h.quadraticCurveTo(cx - openHalf, archTop, cx, archTop);
    h.quadraticCurveTo(cx + openHalf, archTop, cx + openHalf, spring);
    h.lineTo(cx + openHalf, base);
    h.lineTo(cx - openHalf, base);
    shape.holes.push(h);
  });
  const g = new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: false });
  g.translate(0, 0, -depth / 2);
  return g;
}

// A single straight cable/rod between two 3D points.
function Cable({ a, b, r = 0.14, color = "#2f353b" }) {
  const { mid, len, quat } = useMemo(() => {
    const va = new THREE.Vector3(...a);
    const vb = new THREE.Vector3(...b);
    const dir = vb.clone().sub(va);
    const len = dir.length();
    const quat = new THREE.Quaternion().setFromUnitVectors(
      new THREE.Vector3(0, 1, 0),
      dir.clone().normalize()
    );
    return { mid: va.add(vb).multiplyScalar(0.5), len, quat };
  }, [a, b]);
  return (
    <mesh position={mid} quaternion={quat}>
      <cylinderGeometry args={[r, r, len, 5]} />
      <meshStandardMaterial color={color} metalness={0.6} roughness={0.5} />
    </mesh>
  );
}

// Stylized suspension bridge with instanced suspender cables.
function SuspensionBridge({ position, rotation = 0, span = 520, width = 20, deckY = 40, towerH = 46, style = "steel" }) {
  const suspRef = useRef();
  const half = span / 2;
  const towerTop = deckY + towerH;
  const steelColor = "#7d8a94";
  const stoneTex = useMemo(() => (style === "stone" ? makeStone() : null), [style]);
  const gothicGeo = useMemo(
    () => (style === "stone" ? gothicTowerGeo(width + 14, towerTop, deckY, 15) : null),
    [style, width, towerTop, deckY]
  );

  // main cable catenary points across the span (parabola: high at towers, low mid)
  const cablePts = useMemo(() => {
    const pts = [];
    const N = 40;
    for (let i = 0; i <= N; i++) {
      const z = -half + (span * i) / N;
      const f = Math.pow(z / half, 2); // 0 mid -> 1 at towers
      const y = deckY + 4 + f * (towerH - 4);
      pts.push(new THREE.Vector3(0, y, z));
    }
    return pts;
  }, [span, deckY, towerH, half]);

  const cableCurve = useMemo(() => new THREE.CatmullRomCurve3(cablePts), [cablePts]);
  const cableGeo = useMemo(() => new THREE.TubeGeometry(cableCurve, 60, 0.7, 6, false), [cableCurve]);

  const suspenders = useMemo(() => {
    const arr = [];
    const N = 52;
    for (let i = 1; i < N; i++) {
      const z = -half + (span * i) / N;
      const f = Math.pow(z / half, 2);
      const topY = deckY + 4 + f * (towerH - 4);
      const len = topY - deckY;
      if (len < 1) continue;
      arr.push({ z, y: deckY + len / 2, len });
    }
    return arr;
  }, [span, deckY, towerH, half]);

  const stays = useMemo(() => {
    // Diagonal stay cables fanning from each tower top down to the deck (Brooklyn Bridge hallmark).
    const arr = [];
    const n = 6;
    [-half, half].forEach((tz) => {
      [-width / 2, width / 2].forEach((sx) => {
        for (let i = 1; i <= n; i++) {
          const reach = (i / n) * half * 0.85 * (tz < 0 ? 1 : -1);
          arr.push({ a: [sx, towerTop - 2, tz], b: [sx, deckY + 1.6, tz + reach] });
        }
      });
    });
    return arr;
  }, [half, width, towerTop, deckY]);

  useLayoutEffect(() => {
    if (!suspRef.current) return;
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    let idx = 0;
    [-width / 2, width / 2].forEach((sx) => {
      suspenders.forEach((s) => {
        m.compose(new THREE.Vector3(sx, s.y, s.z), q, new THREE.Vector3(1, s.len, 1));
        suspRef.current.setMatrixAt(idx++, m);
      });
    });
    suspRef.current.instanceMatrix.needsUpdate = true;
  }, [suspenders, width]);

  const Tower = ({ z }) => (
    <group position={[0, 0, z]}>
      {style === "stone" ? (
        <>
          <mesh geometry={gothicGeo} castShadow receiveShadow>
            <meshStandardMaterial map={stoneTex} color="#b9bdc0" roughness={0.9} metalness={0.04} />
          </mesh>
          {/* recessed dark soffits inside the two arches for depth */}
          {[-(width + 14) * 0.22, (width + 14) * 0.22].map((cx, i) => (
            <mesh key={i} position={[cx, (deckY + 1.5 + towerTop - 9) / 2, -4]}>
              <boxGeometry args={[(width + 14) * 0.3, towerTop - 9 - (deckY + 1.5), 3]} />
              <meshStandardMaterial color="#33302b" roughness={1} />
            </mesh>
          ))}
        </>
      ) : (
        <>
          {[-width / 2, width / 2].map((x, i) => (
            <mesh key={i} position={[x, towerTop / 2 + 2, 0]} castShadow>
              <boxGeometry args={[4, towerTop + 4, 5]} />
              <meshStandardMaterial color={steelColor} roughness={0.5} metalness={0.6} />
            </mesh>
          ))}
          <mesh position={[0, towerTop - 6, 0]} castShadow>
            <boxGeometry args={[width + 4, 4, 5]} />
            <meshStandardMaterial color={steelColor} roughness={0.5} metalness={0.6} />
          </mesh>
          <mesh position={[0, deckY + 14, 0]} castShadow>
            <boxGeometry args={[width + 4, 3, 5]} />
            <meshStandardMaterial color={steelColor} roughness={0.5} metalness={0.6} />
          </mesh>
        </>
      )}
    </group>
  );

  const totalSusp = suspenders.length * 2;

  return (
    <group position={position} rotation={[0, rotation, 0]}>
      {/* deck (timber walkway tone for stone/Brooklyn) */}
      <mesh position={[0, deckY, 0]} receiveShadow castShadow>
        <boxGeometry args={[width, 1.6, span + 120]} />
        <meshStandardMaterial color={style === "stone" ? "#8a6a45" : "#4a5158"} roughness={0.8} metalness={style === "stone" ? 0.05 : 0.4} />
      </mesh>
      {/* deck side rails */}
      {[-width / 2, width / 2].map((x, i) => (
        <mesh key={i} position={[x, deckY + 1.4, 0]}>
          <boxGeometry args={[0.6, 1.4, span + 120]} />
          <meshStandardMaterial color={style === "stone" ? "#6f563a" : "#5b636b"} roughness={0.7} metalness={0.3} />
        </mesh>
      ))}
      {/* raised timber promenade (Brooklyn Bridge's iconic wooden walkway) */}
      {style === "stone" && (
        <>
          <mesh position={[0, deckY + 1.4, 0]} receiveShadow castShadow>
            <boxGeometry args={[7, 0.6, span + 120]} />
            <meshStandardMaterial color="#9c7748" roughness={0.85} metalness={0.02} />
          </mesh>
          {[-3.4, 3.4].map((x, i) => (
            <mesh key={i} position={[x, deckY + 2.2, 0]}>
              <boxGeometry args={[0.3, 1.2, span + 120]} />
              <meshStandardMaterial color="#6f563a" roughness={0.8} />
            </mesh>
          ))}
        </>
      )}
      <Tower z={-half} />
      <Tower z={half} />
      {/* main cables both sides */}
      {[-width / 2, width / 2].map((x, i) => (
        <mesh key={i} geometry={cableGeo} position={[x, 0, 0]}>
          <meshStandardMaterial color="#3f464d" metalness={0.7} roughness={0.4} />
        </mesh>
      ))}
      {/* instanced suspender cables */}
      <instancedMesh ref={suspRef} args={[null, null, totalSusp]}>
        <cylinderGeometry args={[0.12, 0.12, 1, 5]} />
        <meshStandardMaterial color="#2f353b" metalness={0.6} roughness={0.5} />
      </instancedMesh>
      {/* diagonal stay cables */}
      {stays.map((s, i) => (
        <Cable key={i} a={s.a} b={s.b} r={0.16} />
      ))}
    </group>
  );
}

// Brooklyn & Manhattan Bridges spanning the East River from Manhattan into Brooklyn.
export default function Bridges() {
  return (
    <group>
      <SuspensionBridge position={[850, 0, 150]} rotation={Math.PI / 2} span={820} width={22} deckY={42} towerH={52} style="stone" />
      <SuspensionBridge position={[850, 0, -250]} rotation={Math.PI / 2} span={820} width={20} deckY={47} towerH={60} style="steel" />
    </group>
  );
}
